import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { CatalogResponse, Instance, Product } from '@hub/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Alert, Card, Loading, Pill } from '../components/ui';

const COMPOSE_PLACEHOLDER = `services:
  web:
    image: nginx:alpine
    expose:
      - "80"
`;

interface ExposureDraft {
  service: string;
  port: string;
}

export function NewInstancePage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Admin-only: deploy any image directly, bypassing the catalog
  const [image, setImage] = useState('');
  const [name, setName] = useState('');
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    api.get<CatalogResponse>('/api/catalog')
      .then(setCatalog)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load the catalog'));
  }, []);

  async function create(productId: string) {
    setCreating(productId);
    setError(null);
    try {
      const inst = await api.post<Instance & { upError?: string }>('/api/instances', { productId });
      if (inst.upError) {
        // The service exists but did not start; its logs explain why.
        setError(`Created, but the stack failed to start: ${inst.upError}`);
      }
      navigate(`/dashboard/instances/${inst.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the service');
      setCreating(null);
    }
  }

  async function deploy() {
    if (!image.trim()) return;
    setDeploying(true);
    setError(null);
    try {
      const inst = await api.post<Instance>('/api/instances/deploy', {
        image: image.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      navigate(`/dashboard/instances/${inst.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deploy failed');
    } finally {
      setDeploying(false);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>New service</h2>
        <Link to="/dashboard" className="btn-ghost" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}>
          Cancel
        </Link>
      </div>

      {error && <div style={{ marginBottom: '1rem' }}><Alert tone="error">{error}</Alert></div>}

      {!catalog ? <Loading /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {catalog.products.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              busy={creating !== null}
              creating={creating === p.id}
              onCreate={() => { void create(p.id); }}
            />
          ))}
        </div>
      )}

      {catalog?.userComposeAllowed && (
        <ComposeDeployCard
          onDeployed={(id) => navigate(`/dashboard/instances/${id}`)}
          onError={setError}
        />
      )}

      {isAdmin && (
        <Card style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ha-text-muted)', marginTop: 0 }}>
            Deploy from an image (admin)
          </h3>
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
            The image must serve HTTP on port 80. A subdomain and TLS certificate are provisioned automatically.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 16rem' }}>
              <label className="form-label" htmlFor="deploy-image">Image</label>
              <input
                id="deploy-image" className="form-input" placeholder="nginx:alpine"
                value={image} onChange={(e) => setImage(e.target.value)}
              />
            </div>
            <div style={{ flex: '1 1 12rem' }}>
              <label className="form-label" htmlFor="deploy-name">Name (optional)</label>
              <input
                id="deploy-name" className="form-input"
                value={name} onChange={(e) => setName(e.target.value)}
              />
            </div>
            <button type="button" className="btn-primary" disabled={deploying || !image.trim()} onClick={() => { void deploy(); }}>
              {deploying ? 'Deploying…' : 'Deploy'}
            </button>
          </div>
        </Card>
      )}
    </>
  );
}

function ProductCard({ product, busy, creating, onCreate }: {
  product: Product;
  busy: boolean;
  creating: boolean;
  onCreate: () => void;
}) {
  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ background: product.bgColor, padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span style={{ fontSize: '1.5rem' }}>{product.icon}</span>
        <span style={{ color: product.accentColor, fontWeight: 600 }}>{product.name}</span>
      </div>
      <div style={{ padding: '1rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Pill tone={product.kind === 'compose' ? 'emerald' : 'muted'}>
            {product.kind === 'compose' ? 'multi-container' : 'single container'}
          </Pill>
        </div>
        <p className="muted" style={{ fontSize: '0.85rem', marginTop: 0 }}>{product.description}</p>
        {!product.available && (
          <div style={{ marginBottom: '0.5rem' }}>
            <Alert tone="warn">This host&apos;s container backend cannot run compose stacks.</Alert>
          </div>
        )}
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%' }}
          disabled={busy || !product.available}
          onClick={onCreate}
        >
          {creating ? 'Creating…' : 'Create'}
        </button>
      </div>
    </Card>
  );
}

/**
 * Deploying a compose file the user writes. Ports are never published by the
 * file itself — each endpoint listed here gets a hub-allocated port, a
 * subdomain, and a Caddy route.
 */
function ComposeDeployCard({ onDeployed, onError }: {
  onDeployed: (instanceId: number) => void;
  onError: (message: string | null) => void;
}) {
  const [name, setName] = useState('');
  const [yaml, setYaml] = useState('');
  const [exposures, setExposures] = useState<ExposureDraft[]>([{ service: '', port: '' }]);
  const [busy, setBusy] = useState(false);

  const valid = yaml.trim().length > 0
    && exposures.length > 0
    && exposures.every((e) => e.service.trim() && Number(e.port) > 0);

  function update(index: number, patch: Partial<ExposureDraft>) {
    setExposures((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }

  async function submit() {
    setBusy(true);
    onError(null);
    try {
      const inst = await api.post<Instance & { upError?: string }>('/api/instances/compose', {
        ...(name.trim() ? { name: name.trim() } : {}),
        composeYaml: yaml,
        exposures: exposures.map((e, i) => ({
          service: e.service.trim(),
          port: Number(e.port),
          primary: i === 0,
        })),
      });
      if (inst.upError) onError(`Created, but the stack failed to start: ${inst.upError}`);
      onDeployed(inst.id);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Deploy failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card style={{ marginTop: '1.5rem' }}>
      <h3 style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ha-text-muted)', marginTop: 0 }}>
        Deploy your own compose file
      </h3>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Do not add a <code>ports:</code> section — list the endpoints you want published below and the hub
        allocates the port, subdomain, and certificate. Bind mounts, privileged mode, and host networking are rejected.
        The first endpoint becomes the service&apos;s main address.
      </p>

      <div style={{ marginBottom: '0.75rem' }}>
        <label className="form-label" htmlFor="compose-name">Name (optional)</label>
        <input
          id="compose-name" className="form-input" style={{ maxWidth: '20rem' }}
          value={name} onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <label className="form-label" htmlFor="compose-yaml">docker-compose.yml</label>
        <textarea
          id="compose-yaml"
          className="form-input mono"
          rows={12}
          spellCheck={false}
          placeholder={COMPOSE_PLACEHOLDER}
          value={yaml}
          onChange={(e) => setYaml(e.target.value)}
        />
      </div>

      <label className="form-label">Published endpoints</label>
      {exposures.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 12rem' }}>
            <input
              className="form-input mono" placeholder="service name"
              value={e.service} onChange={(ev) => update(i, { service: ev.target.value })}
            />
          </div>
          <div style={{ flex: '0 1 8rem' }}>
            <input
              className="form-input mono" placeholder="port" inputMode="numeric"
              value={e.port} onChange={(ev) => update(i, { port: ev.target.value })}
            />
          </div>
          {i === 0
            ? <Pill tone="emerald">main</Pill>
            : (
              <button
                type="button" className="btn-ghost"
                onClick={() => setExposures((prev) => prev.filter((_, idx) => idx !== i))}
              >Remove</button>
            )}
        </div>
      ))}
      <button
        type="button" className="btn-ghost" style={{ marginBottom: '0.75rem' }}
        onClick={() => setExposures((prev) => [...prev, { service: '', port: '' }])}
      >Add endpoint</button>

      <div>
        <button type="button" className="btn-primary" disabled={busy || !valid} onClick={() => { void submit(); }}>
          {busy ? 'Deploying…' : 'Deploy stack'}
        </button>
      </div>
    </Card>
  );
}
